// Helpers for the per-customer collection due date (mirrors the Android feature).
// Due dates are stored as epoch millis; "not set" is undefined or 0.

export interface IDueDateLabel {
  // Human-readable status, e.g. "Collect today", "Collect on 25 Jul 2026",
  // "Pending collection since 3 days".
  text: string
  // True when the due date is in the past (overdue) — render in the error color.
  isOverdue: boolean
  // True when due today.
  isToday: boolean
}

// Raw check — "is a due date set at all". Use this for the due-date *setter* UI, where the
// merchant must always see (and be able to clear) whatever they picked.
export const hasDueDate = (dueDate?: number): boolean => typeof dueDate === 'number' && dueDate > 0

// The gated check the ledger list uses — a port of Android's
// SearchAndSortUtils.hasGatedDueDate:
//   customer.dueActive && customer.dueInfo_activeDate != null && customer.balanceV2 < 0
// The first two conditions are already applied upstream when the dues are fetched
// (dashboard.effects.ts filters on `is_due_active` && `active_date > 0`), so what's left
// here is the balance gate: a due date only counts while the customer still OWES money.
// Web's balance sign matches Android's balanceV2 — negative = customer owes, positive =
// advance (see Customer.List.tsx `isAmountDue={customer.balance <= 0}`). A missing balance
// is treated as 0, i.e. not owing, so it does not float.
export const hasGatedDueDate = (entity: { dueDate?: number; balance?: number }): boolean =>
  hasDueDate(entity.dueDate) && (entity.balance || 0) < 0

// The single "last activity" value every customer sort orders by — the web equivalent of the
// lastActivity column legacy Android aggregates in BackendCoreDatabase.kt. That CASE ends in
// `ELSE Customer.createdAt`, and with no transactions at all it is the only branch that can
// fire, so Android's value is never below the customer's own createdAt. Verified against the
// prod app: a customer with zero transactions sorts at its creation time, not at the bottom.
// Hence max(), not `lastActivityAt || createdAt` — a lastActivity older than the account
// itself must still floor at createdAt, exactly as the SQL does.
export const customerActivityAt = (customer: {
  lastActivityAt?: number
  createdAt?: number
}): number => Math.max(Number(customer.lastActivityAt) || 0, Number(customer.createdAt) || 0)

// Carries known due dates across a customer-list refetch. The list API does not return them
// — they arrive from the separate ListDuesInfo call — so a refetch would otherwise blank
// `dueDate` on every customer until that second request lands ~125ms later. That gap is
// visible: the Default sort buckets on hasGatedDueDate, so with the dates missing the
// due-today/overdue buckets collapse, the list re-sorts by plain recency, and then snaps
// back once the dues arrive. Mirrors withDerivedActivity, which exists for the same reason
// on lastActivity. SET_CUSTOMER_DUES_INFO remains authoritative — it clears any date this
// preserved but the server no longer reports.
export const withPreservedDueDate = <T extends { id: string; dueDate?: number }>(
  entities: T[] | undefined,
  existingById?: Record<string, { dueDate?: number }>
): T[] | undefined => {
  if (!entities || !existingById) {
    return entities
  }
  return entities.map((entity) => {
    const known = existingById[entity.id]?.dueDate
    return entity.dueDate === undefined && known !== undefined
      ? { ...entity, dueDate: known }
      : entity
  })
}

// Whole-day difference (dueDate - today), ignoring the time of day.
// Positive = future, 0 = today, negative = overdue.
export const daysUntilDueDate = (dueDate: number, now: number = Date.now()): number => {
  const startOfDay = (ms: number) => {
    const d = new Date(ms)
    d.setHours(0, 0, 0, 0)
    return d.getTime()
  }
  const MS_PER_DAY = 24 * 60 * 60 * 1000
  return Math.round((startOfDay(dueDate) - startOfDay(now)) / MS_PER_DAY)
}

// Human overdue duration, mirroring the post-PR Android CustomerRow logic: up to 30 days
// reads in days, beyond that in months (days / 30) — no separate "1 month" bucket and no
// years (e.g. 30d → "30 days", 67d → "2 months", 400d → "13 months").
export const formatOverdueDuration = (daysDiff: number): string => {
  const days = Math.abs(daysDiff)
  if (days === 0) {
    return ''
  }
  if (days <= 30) {
    return `${days} day${days === 1 ? '' : 's'}`
  }
  const months = Math.floor(days / 30)
  return `${months} month${months === 1 ? '' : 's'}`
}

// Compact date, e.g. "30 Jul, 2026" (comma before the year — Android-parity style).
export const formatDueDateShort = (dueDate: number): string => {
  try {
    return new Date(dueDate)
      .toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
      .replace(/,/g, '')
      .replace(/ (\d{4})$/, ', $1')
  } catch {
    return ''
  }
}

// Builds the label + styling flags for a customer's due date.
// `translate` is optional; when omitted, English fallbacks are used so this stays
// usable outside React (e.g. in sort utilities).
export const getDueDateLabel = (
  dueDate: number,
  translate?: (key: string, opts?: Record<string, unknown>) => string,
  now: number = Date.now()
): IDueDateLabel => {
  const days = daysUntilDueDate(dueDate, now)
  const t = (key: string, fallback: string, opts?: Record<string, unknown>) =>
    translate ? translate(key, opts) : fallback

  if (days === 0) {
    return { text: t('dashboard:Collect Today', 'Collect Today'), isOverdue: false, isToday: true }
  }
  if (days < 0) {
    // Four whole sentences rather than "Pending collection since" + a formatted
    // duration: word order and the day/month plural differ per language (Android
    // plurals pending_day / pending_month).
    const overdue = Math.abs(days)
    const useMonths = overdue > 30
    const count = useMonths ? Math.floor(overdue / 30) : overdue
    const key = useMonths
      ? count === 1
        ? 'dashboard:Pending collection since 1 month'
        : 'dashboard:Pending collection since {{count}} months'
      : count === 1
      ? 'dashboard:Pending collection since 1 day'
      : 'dashboard:Pending collection since {{count}} days'
    return {
      text: t(key, `Pending collection since ${formatOverdueDuration(days)}`, { count }),
      isOverdue: true,
      isToday: false
    }
  }
  return {
    text: t('dashboard:Collect on date', `Collect on ${formatDueDateShort(dueDate)}`, {
      date: formatDueDateShort(dueDate)
    }),
    isOverdue: false,
    isToday: false
  }
}

// The "Default" customer sort — a faithful port of legacy Android's getDefaultCustomerList
// (android/home/.../HomeSearchAndSortUtils.kt). Buckets customers into due-today → overdue →
// everyone else (no due date, ungated due date, and future due date), and orders EACH bucket
// by most-recent activity, then concatenates. Due dates float the first two groups to the
// top; picking any other sort ignores due dates entirely.
//
// Two details are deliberate legacy parity and NOT arbitrary:
//   1. Bucketing uses hasGatedDueDate, so a settled / advance-paid customer does not float
//      even while the backend still reports an active due date.
//   2. Ties break on customerActivityAt — see the note there on why createdAt matters.
export const getDefaultSortedCustomers = <
  T extends { dueDate?: number; balance?: number; lastActivityAt?: number; createdAt?: number }
>(
  customers: T[],
  now: number = Date.now()
): T[] => {
  if (!customers || customers.length === 0) {
    return customers || []
  }
  const dueToday: T[] = []
  const overdue: T[] = []
  const rest: T[] = []
  customers.forEach((c) => {
    if (!hasGatedDueDate(c)) {
      rest.push(c)
      return
    }
    const days = daysUntilDueDate(c.dueDate as number, now)
    if (days === 0) {
      dueToday.push(c)
    } else if (days < 0) {
      overdue.push(c)
    } else {
      rest.push(c) // future due date is not floated
    }
  })
  const byRecencyDesc = (a: T, b: T) => customerActivityAt(b) - customerActivityAt(a)
  return [
    ...dueToday.sort(byRecencyDesc),
    ...overdue.sort(byRecencyDesc),
    ...rest.sort(byRecencyDesc)
  ]
}
